// TargetingMultiSelectCard.js — AS-117
// Generalised multi-select targeting card.
//
// Props:
//   title       string             — card title (e.g. "裝置", "年齡")
//   icon        ReactNode          — inline SVG icon
//   options     [{value, label}]   — enum options
//   state       { all, selected }  or { all, mode, selected } — controlled value
//   onChange    (newState) => void
//   allowMode   boolean            — true: show Include/Exclude dropdown (device/os/browser)
//                                    false: hide dropdown, state has no mode (age/gender)
//
// When allowMode=false, state shape is { all: bool, selected: string[] }
// When allowMode=true,  state shape is { all: bool, mode: string, selected: string[] }

import React from 'react';
import '../ds/TargetingSection.css';
import '../DeviceOsTargetingCard/DeviceOsTargetingCard.css';

function TargetingMultiSelectCard({ title, icon, options, state, onChange, allowMode = true }) {
  const { all, mode, selected } = state;

  const allValues = options.map((o) => o.value);
  const allSelected = allValues.length > 0 && allValues.every((v) => selected.includes(v));
  const someSelected = selected.length > 0 && !allSelected;

  // Build summary string shown below title
  const summary = all
    ? null
    : selected.length > 0
    ? allowMode
      ? `${mode === 'exclude' ? '排除' : '包含'} ${selected
          .map((v) => options.find((o) => o.value === v)?.label || v)
          .join('、')}`
      : selected.map((v) => options.find((o) => o.value === v)?.label || v).join('、')
    : null;

  const handleAllChange = (checked) => {
    if (checked) {
      if (allowMode) {
        onChange({ all: true, mode: mode || 'include', selected: [] });
      } else {
        onChange({ all: true, selected: [] });
      }
    } else {
      // Uncheck All → default to select all below (prevent empty invalid state)
      if (allowMode) {
        onChange({ all: false, mode: mode || 'include', selected: allValues });
      } else {
        onChange({ all: false, selected: allValues });
      }
    }
  };

  const handleModeChange = (e) => {
    onChange({ all, mode: e.target.value, selected });
  };

  const handleSelectAllChange = (checked) => {
    if (allowMode) {
      onChange({ all, mode, selected: checked ? allValues : [] });
    } else {
      onChange({ all, selected: checked ? allValues : [] });
    }
  };

  const handleOptionChange = (value, checked) => {
    const next = checked
      ? [...selected, value]
      : selected.filter((v) => v !== value);
    if (allowMode) {
      onChange({ all, mode, selected: next });
    } else {
      onChange({ all, selected: next });
    }
  };

  return (
    <div className={`targeting-section${!all ? ' targeting-section--enabled' : ''} device-os-card`}>
      {/* Header row */}
      <div className="targeting-section__header device-os-card__header" onClick={() => handleAllChange(!all)}>
        <div className="targeting-section__icon">{icon}</div>
        <div className="targeting-section__info">
          <h3 className="targeting-section__title">{title}</h3>
          {summary && <p className="targeting-section__summary">{summary}</p>}
        </div>
        {/* All XXX checkbox */}
        <label
          className="device-os-card__all-label"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="device-os-card__checkbox"
            checked={all}
            onChange={(e) => handleAllChange(e.target.checked)}
            aria-label={`全部 ${title}`}
          />
          <span className="device-os-card__all-text">全部</span>
        </label>
      </div>

      {/* Body — visible when not all */}
      {!all && (
        <div className="targeting-section__body device-os-card__body">
          {/* Include / Exclude dropdown — only when allowMode=true */}
          {allowMode && (
            <div className="device-os-card__mode-row">
              <select
                value={mode}
                onChange={handleModeChange}
                className="device-os-card__mode-select"
                aria-label={`${title} 鎖定模式`}
              >
                <option value="include">包含（Include）</option>
                <option value="exclude">排除（Exclude）</option>
              </select>
            </div>
          )}

          {/* Select all below */}
          <label className="device-os-card__select-all-label">
            <input
              type="checkbox"
              className="device-os-card__checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
            />
            <span>全選下方選項</span>
          </label>

          {/* Option checkboxes grid */}
          <div className="device-os-card__options">
            {options.map((opt) => (
              <label key={opt.value} className="device-os-card__option-label">
                <input
                  type="checkbox"
                  className="device-os-card__checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={(e) => handleOptionChange(opt.value, e.target.checked)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TargetingMultiSelectCard;
